import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { CATEGORIES, TIERS } from '../utils/scoring';

const QUARTERS = ['Q1 2026', 'Q2 2026', 'Q3 2026', 'Q4 2026', 'Q1 2027', 'Q2 2027'];

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('spirits');
  const [activeQuarter, setActiveQuarter] = useState('Q2 2026');
  const [editingPkg, setEditingPkg] = useState(null);
  const [editAmounts, setEditAmounts] = useState({});
  const [newSupplier, setNewSupplier] = useState({ name: '', skuCount: 1, skus: '' });

  useEffect(() => { loadPackages(); }, []);

  const loadPackages = async () => {
    const { data } = await supabase.from('bc_packages').select('*').order('tier');
    setPackages(data || []);
    setLoading(false);
  };

  const getPackage = (cat, tier, quarter) => packages.find(p => p.category === cat && p.tier === tier && p.quarter === quarter);

  const savePackage = async (cat, tier, quarter, monthlyTotal) => {
    const existing = getPackage(cat, tier, quarter);
    if (existing) {
      // Update local state immediately
      setPackages(prev => prev.map(p => p.id === existing.id ? { ...p, monthly_total: monthlyTotal } : p));
      await supabase.from('bc_packages').update({ monthly_total: monthlyTotal, updated_at: new Date().toISOString() }).eq('id', existing.id);
    } else {
      const { data } = await supabase.from('bc_packages').insert({ category: cat, tier, quarter, monthly_total: monthlyTotal, suppliers: [] }).select().single();
      if (data) setPackages(prev => [...prev, data]);
    }
  };

  const addSupplierToPkg = async (pkgId, supplier) => {
    const pkg = packages.find(p => p.id === pkgId);
    if (!pkg) return;
    const suppliers = [...(pkg.suppliers || []), { ...supplier, id: Date.now(), status: 'proposed' }];
    await supabase.from('bc_packages').update({ suppliers, updated_at: new Date().toISOString() }).eq('id', pkgId);
    loadPackages();
    setNewSupplier({ name: '', skuCount: 1, skus: '' });
  };

  const removeSupplier = async (pkgId, supplierId) => {
    const pkg = packages.find(p => p.id === pkgId);
    if (!pkg) return;
    const suppliers = (pkg.suppliers || []).filter(s => s.id !== supplierId);
    await supabase.from('bc_packages').update({ suppliers }).eq('id', pkgId);
    loadPackages();
  };

  const updateSupplierStatus = async (pkgId, supplierId, status) => {
    const pkg = packages.find(p => p.id === pkgId);
    if (!pkg) return;
    const suppliers = (pkg.suppliers || []).map(s => s.id === supplierId ? { ...s, status } : s);
    await supabase.from('bc_packages').update({ suppliers }).eq('id', pkgId);
    loadPackages();
  };

  const isLiquor = activeCategory === 'spirits';
  const fmt = n => '$' + Math.round(n || 0).toLocaleString();
  const catInfo = CATEGORIES.find(c => c.id === activeCategory);

  // Calculate supplier costs for a package
  const calcSupplierCost = (pkg) => {
    if (!pkg || !pkg.monthly_total) return [];
    const suppliers = pkg.suppliers || [];
    if (isLiquor) {
      // Per-SKU: total / totalSKUs * supplierSKUs
      const totalSkus = suppliers.reduce((s, sup) => s + (sup.skuCount || 1), 0);
      if (totalSkus === 0) return suppliers.map(s => ({ ...s, monthlyCost: 0, quarterlyCost: 0 }));
      const perSku = pkg.monthly_total / totalSkus;
      return suppliers.map(s => ({
        ...s,
        monthlyCost: Math.round(perSku * (s.skuCount || 1)),
        quarterlyCost: Math.round(perSku * (s.skuCount || 1) * 3),
        perSku: Math.round(perSku),
      }));
    } else {
      // Even split for beer/wine/NA
      const count = suppliers.length || 1;
      const each = pkg.monthly_total / count;
      return suppliers.map(s => ({
        ...s,
        monthlyCost: Math.round(each),
        quarterlyCost: Math.round(each * 3),
      }));
    }
  };

  if (loading) return <div className="py-20 text-center text-neutral-500">Loading...</div>;

  return (
    <div className="py-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl text-white">Support Packages</h1>
          <p className="text-xs text-neutral-500">Manage category support amounts and supplier commitments</p>
        </div>
        <select value={activeQuarter} onChange={e => setActiveQuarter(e.target.value)} className="input-field text-sm py-2 w-32">
          {QUARTERS.map(q => <option key={q}>{q}</option>)}
        </select>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 mb-6">
        {CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${activeCategory === cat.id ? 'bg-brand-gold/15 text-brand-gold border border-brand-gold/40' : 'bg-neutral-800/40 text-neutral-500 border border-neutral-800'}`}>
            {cat.icon} {cat.name}
          </button>
        ))}
      </div>

      {/* Pricing model note */}
      <div className="card p-4 mb-4">
        <p className="text-[10px] text-neutral-500">
          {isLiquor
            ? '💡 Liquor: Total package divided by total SKUs across all suppliers. Each supplier pays per-SKU rate × their SKU count.'
            : `💡 ${catInfo?.name}: Total package divided evenly across all participating suppliers.`
          }
        </p>
      </div>

      {/* Tier packages */}
      <div className="space-y-4">
        {TIERS.map((tier, tierIdx) => {
          const tierNum = tierIdx + 1;
          const pkg = getPackage(activeCategory, tierNum, activeQuarter);
          const supplierCosts = calcSupplierCost(pkg);
          const totalCommitted = supplierCosts.reduce((s, sup) => s + (sup.status === 'committed' || sup.status === 'active' ? sup.monthlyCost : 0), 0);
          const totalProposed = supplierCosts.reduce((s, sup) => s + sup.monthlyCost, 0);

          return (
            <div key={tier.name} className="card overflow-hidden" style={{ borderLeft: `4px solid ${tier.color}` }}>
              <div className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-sm font-semibold text-white">Tier {tierNum} — {tier.name}</h2>
                    <p className="text-[10px] text-neutral-600">{tier.desc}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <label className="text-[9px] text-neutral-600 block">Monthly Total</label>
                      <input type="number" placeholder="0"
                        value={editAmounts[`${activeCategory}_${tierNum}`] !== undefined ? editAmounts[`${activeCategory}_${tierNum}`] : (pkg?.monthly_total || '')}
                        onChange={e => setEditAmounts({ ...editAmounts, [`${activeCategory}_${tierNum}`]: e.target.value })}
                        onBlur={e => {
                          const val = +e.target.value;
                          savePackage(activeCategory, tierNum, activeQuarter, val);
                          setEditAmounts(prev => { const n = { ...prev }; delete n[`${activeCategory}_${tierNum}`]; return n; });
                        }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                        className="input-field text-sm py-1.5 w-28 text-right font-bold" style={{ color: tier.color }} />
                    </div>
                    {pkg?.monthly_total > 0 && (
                      <div className="text-right">
                        <p className="text-[9px] text-neutral-600">Quarterly</p>
                        <p className="text-sm font-bold" style={{ color: tier.color }}>{fmt(pkg.monthly_total * 3)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Funding status */}
                {pkg?.monthly_total > 0 && (
                  <div className="flex items-center gap-4 mb-4 text-[10px]">
                    <span className="text-neutral-500">Target: <span className="text-white font-semibold">{fmt(pkg.monthly_total)}/mo</span></span>
                    <span className="text-neutral-500">Committed: <span className="text-emerald-400 font-semibold">{fmt(totalCommitted)}</span></span>
                    <span className="text-neutral-500">Proposed: <span className="text-amber-400 font-semibold">{fmt(totalProposed)}</span></span>
                    <span className="text-neutral-500">Gap: <span className={totalProposed >= pkg.monthly_total ? 'text-emerald-400' : 'text-red-400'}>{fmt(Math.max(0, pkg.monthly_total - totalProposed))}</span></span>
                    {/* Progress bar */}
                    <div className="flex-1 h-2 bg-neutral-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (totalProposed / pkg.monthly_total) * 100)}%`, backgroundColor: totalProposed >= pkg.monthly_total ? '#34d399' : '#fbbf24' }} />
                    </div>
                  </div>
                )}

                {/* Suppliers */}
                {pkg?.monthly_total > 0 && (
                  <>
                    <div className="space-y-2 mb-3">
                      {supplierCosts.map(sup => (
                        <div key={sup.id} className="flex items-center gap-3 bg-neutral-800/20 rounded-lg p-3">
                          <div className="flex-1">
                            <p className="text-xs font-medium text-white">{sup.name}</p>
                            <p className="text-[10px] text-neutral-500">
                              {isLiquor ? `${sup.skuCount} SKU(s)${sup.skus ? ` — ${sup.skus}` : ''} · ${fmt(sup.perSku)}/SKU` : 'Equal share'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-bold text-white">{fmt(sup.monthlyCost)}/mo</p>
                            <p className="text-[9px] text-neutral-600">{fmt(sup.quarterlyCost)}/qtr</p>
                          </div>
                          <select value={sup.status} onChange={e => updateSupplierStatus(pkg.id, sup.id, e.target.value)}
                            className={`text-[9px] px-2 py-1 rounded font-medium border-0 ${
                              sup.status === 'active' || sup.status === 'committed' ? 'bg-emerald-500/15 text-emerald-400' :
                              sup.status === 'declined' ? 'bg-red-500/15 text-red-400' :
                              'bg-amber-500/15 text-amber-400'
                            }`}>
                            <option value="proposed">Proposed</option>
                            <option value="committed">Committed</option>
                            <option value="active">Active</option>
                            <option value="declined">Declined</option>
                          </select>
                          <button onClick={() => removeSupplier(pkg.id, sup.id)} className="text-neutral-700 hover:text-red-400 text-[10px]">×</button>
                        </div>
                      ))}
                    </div>

                    {/* Add supplier */}
                    <div className="flex flex-wrap gap-2">
                      <input value={newSupplier.name} onChange={e => setNewSupplier({ ...newSupplier, name: e.target.value })}
                        placeholder="Supplier name" className="input-field text-xs py-1.5 w-36" />
                      {isLiquor && (
                        <>
                          <input type="number" value={newSupplier.skuCount} min={1} onChange={e => setNewSupplier({ ...newSupplier, skuCount: +e.target.value })}
                            placeholder="SKUs" className="input-field text-xs py-1.5 w-16" />
                          <input value={newSupplier.skus} onChange={e => setNewSupplier({ ...newSupplier, skus: e.target.value })}
                            placeholder="SKU names (optional)" className="input-field text-xs py-1.5 w-40" />
                        </>
                      )}
                      <button onClick={() => pkg && addSupplierToPkg(pkg.id, newSupplier)} disabled={!newSupplier.name}
                        className="text-[10px] px-3 py-1.5 rounded bg-brand-gold/10 text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/20 disabled:opacity-40 transition-colors">
                        + Add Supplier
                      </button>
                    </div>
                  </>
                )}

                {!pkg?.monthly_total && (
                  <p className="text-neutral-700 text-xs text-center py-2">Set a monthly total to start adding suppliers.</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-[8px] text-neutral-700 mt-8">
        Supplier marketing support funds are administered for legitimate marketing, educational, activation, and consumer engagement activities.
        No provision constitutes payment for guaranteed product placement, exclusive purchasing, or exclusion of competing products.
      </p>
    </div>
  );
}
